import React, { useState, useEffect } from 'react';
import { Container, Table, Form } from 'react-bootstrap';
import { Employee, ProjectModel, ProjectModel_DB } from '@models/Models';
import { ProjectStatus } from '@models/Enums';
import useEmployee from '@hooks/useEmployee';
import useProject from '@hooks/useProject';
import MyButton from '@components/MyButton';

const ProjectsOverview = () => {
  const [loading, setLoading] = useState(false);

  // Employee variables
  const { getAllEmployees } = useEmployee();
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);

  // Project variables
  const { getAllProjects, updateProject } = useProject();
  const [filteredProjects, setFilteredProjects] = useState<ProjectModel[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: keyof Employee | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc',
  });

  useEffect(() => {
    employeeGetter();
    projectsGetter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employeeGetter = async () => {
    try {
      const employeesResults = await getAllEmployees();
      
      if (!employeesResults.success || !employeesResults.data) {
        console.error(employeesResults.message);
        alert(employeesResults.message);
        return;
      };

      console.log(employeesResults)
      setFilteredEmployees(employeesResults.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const projectsGetter = async () => {
    try {
      const projectsResponse = await getAllProjects();
      if (!projectsResponse.success) {
        console.error('Error fetching projects:', projectsResponse.message);
        alert(projectsResponse.message);
        return;
      }
      const notArchivedProjects = projectsResponse.data
        .filter(
          (project: ProjectModel) =>
            project.status !== ProjectStatus.ARCHIVED
        )
        .map((project: ProjectModel) => ({
          ...project,
          employeeTeam: project.employeeTeam ?? [],
        }));

      setFilteredProjects(notArchivedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const checkEmployeeAgainstProjects = (
    employeeProjects: ProjectModel[] | undefined,
    projectId: string
  ) => {
    return (employeeProjects ?? []).some(
      (project) => project.id === projectId
    );
  };

  const toggleProjectStatus = (projectId: string) => {
    setFilteredProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === projectId
          ? {
              ...project,
              status: project.status === ProjectStatus.ACTIV ? ProjectStatus.INACTIV : ProjectStatus.ACTIV,
            }
          : project
      )
    );
  };

  const addRemoveEmployeeToProject = (
  employeeId: string,
  projectId: string
) => {
  setFilteredProjects((prevProjects) =>
    prevProjects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      const employeeTeam = project.employeeTeam ?? [];

      return {
        ...project,
        employeeTeam: employeeTeam.includes(employeeId)
          ? employeeTeam.filter((id) => id !== employeeId)
          : [...employeeTeam, employeeId],
      };
    })
  );
};

  const handleSave = async () => {  
    for (const project of filteredProjects) {
      try {
        setLoading(true);

        const myEmployeeTeam: string[] = project.employeeTeam ?? [];

        const dbProject: Partial<ProjectModel_DB> = {
          status: project.status,
          employeeTeam: myEmployeeTeam,
        };

        console.log(dbProject)

        const response = await updateProject(project.id as string, dbProject);
  
        if (!response.success) {
          console.error(`Error updating Project ${project.id}:`, response.message);
          alert(`Fehler beim Speichern des Projekt ${project.name}: ${response.message}`);
          return;
        }
      } catch (error) {
        console.error(`Error updating project ${project.id}:`, error);
        alert(`Fehler beim Speichern des Projeckt ${project.name}.`);
        return;
      }finally{
        setLoading(false);
      }
    }
  
    alert('Alle Änderungen wurden erfolgreich gespeichert.');
    await employeeGetter(); // Refresh employee data
    await projectsGetter(); // Refresh project data
  };

  const handleSort = (key: keyof Employee) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedEmployees = [...filteredEmployees].sort((a, b) => {
      const aValue = a[key] as string;
      const bValue = b[key] as string;

      return direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    setFilteredEmployees(sortedEmployees);
  };

  const handleArchiveProject = (projectId: string) => {
    setFilteredProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === projectId
          ? {
              ...project,
              status:
                project.status === ProjectStatus.ARCHIVED
                  ? ProjectStatus.INACTIV
                  : ProjectStatus.ARCHIVED,
            }
          : project
      )
    );
  };

  
  return (
    <Container fluid className="myCont">
      <div className="d-flex flex-row gap-2 py-2 justify-content-end">
        <MyButton title={loading ? "Loading..." : 'Speichern'} disabled={loading} isSecundary={false} onClick={handleSave} />
        <MyButton
          title="Zurücksetzen"
          disabled={false}
          isSecundary={true}
          onClick={() => {
            employeeGetter();
            projectsGetter();
          }}
        />
      </div>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th>Projekt archivieren/inaktiv</th>
            {filteredProjects.map((project) => (
              <th key={project.id}>
                <Form.Check
                  type="checkbox"
                  checked={project.status === ProjectStatus.ARCHIVED}
                  onChange={() => handleArchiveProject(project.id as string)}
                />
              </th>
            ))}
          </tr>
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th>Projekt aktiv:</th>
            {filteredProjects.map((project) => (
              <th key={project.id}>
                <Form.Check
                  type="checkbox"
                  checked={project.status === ProjectStatus.ACTIV}
                  onChange={() => toggleProjectStatus(project.id as string)}
                />
              </th>
            ))}
          </tr>
          <tr>
            <th>Firma</th>
            <th onClick={() => handleSort('firstName')}>Vorname</th>
            <th onClick={() => handleSort('lastName')}>Nachname</th>
            <th onClick={() => handleSort('email')}>Email</th>
            <th onClick={() => handleSort('phone')}>Handy</th>
            {filteredProjects.map((project) => (
              <th className="vertical-header" key={project.id}>
                {project.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredEmployees.map((employee) => (
            <tr key={employee.id}>
              <td>Keine Firma</td>
              <td>{employee.firstName}</td>
              <td>{employee.lastName}</td>
              <td>{employee.email}</td>
              <td>{employee.phone}</td>
              {filteredProjects.map((project) => (
                <td key={`${employee.id}-${project.id}`}>
                  <Form.Check
                    type="checkbox"
                    checked={(project.employeeTeam ?? []).includes(employee.id as string)}
                    onChange={() => addRemoveEmployeeToProject(employee.id as string, project.id as string)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default ProjectsOverview;
